/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
import { categoryById, productBySku } from '../../queries/2.3.5';
import { categoryByUID, productBySku as productBySkuUID } from '../../queries/2.4.2ce';
import { browse_config, browse_root_route, browse_category, browse_product } from './urls';
import { mapBreadcrumb } from './utils';

function _mapCategory(category) {
    const newCategory = {
        ...category,
        identifier: category.uid ? category.uid : category.id.toString()
    };
    delete newCategory.id;
    delete newCategory.uid;

    return newCategory;
}

export function generateUrlFromState(state) {
    const { config, categories, configurableProduct } = state;
    if (configurableProduct) {
        return browse_product(config.identifier, configurableProduct.sku);
    }
    if (categories && categories.length > 0) {
        const lastCategory = categories[categories.length - 1];
        return browse_category(state.config.identifier, lastCategory.identifier);
    }
    if (config) {
        return browse_config(config.identifier);
    }
    return browse_root_route;
}

export function convertStateToHistoryState(state) {
    const { categories, configurableProduct, config } = state;
    return {
        ProductAdminContext: {
            categories,
            configurableProduct,
            config: config ? config.identifier : null
        }
    };
}

export function restoreStateFromHistoryState(configs, historyState) {
    const { categories, configurableProduct, config } = historyState.ProductAdminContext;
    const newConfig = config ? configs.find(c => c.identifier === config) || null : null;
    return {
        categories,
        configurableProduct,
        config: newConfig,
        configs
    };
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export async function extractStateFromUrl(state, dispatch, configs, params) {
    const newState = { loading: false, configs };

    // Reconstruct state from URL
    const { configId, categoryIdentifier, productSku } = params;
    const { config, categories, configurableProduct } = state;

    // Case 1: No config set, but config in URL
    // Case 2: config set, but does not exist in the configs (anymore)
    if (configId && (!config || (config && !configs.find(c => c.identifier === config.identifier)))) {
        const newConfig = configs.find(c => c.identifier === configId);
        if (newConfig) {
            newState.config = newConfig;
        } else {
            newState.config = null;
        }
    }

    // Case 3: No category set, but category in URL
    if (categories && categories.length === 0 && categoryIdentifier && newState.config) {
        // Fetch category with breadcrumbs
        const { gqlClient, supportUID } = newState.config;
        try {
            const result = supportUID
                ? await gqlClient.query({
                      query: categoryByUID,
                      variables: { uid: decodeURIComponent(categoryIdentifier) }
                  })
                : await gqlClient.query({
                      query: categoryById,
                      variables: { id: decodeURIComponent(categoryIdentifier) }
                  });

            const category = _mapCategory(result.data.categoryList[0]);
            const breadcrumbs = category.breadcrumbs || [];

            // Restore category path
            const categories = [...breadcrumbs.map(mapBreadcrumb), category];
            newState.categories = categories;
        } catch (err) {
            console.error('Could not fetch category', err);
        }
    }

    // Case 4: No product set, but product in URL
    if (!configurableProduct && productSku && newState.config) {
        // Fetch product with categories and breadcrumbs
        const { gqlClient, supportUID } = newState.config;
        try {
            const query = supportUID ? productBySkuUID : productBySku;
            const result = await gqlClient.query({ query: query, variables: { sku: productSku } });
            const product = result.data.products.items[0];

            // Product can be in multiple categories. We cannot know from just the SKU where the user is. So just
            // take the last one, which might be the most specific one.
            const category = _mapCategory(product.categories[product.categories.length - 1]);
            const breadcrumbs = category.breadcrumbs || [];

            // Restore category path
            const categories = [...breadcrumbs.map(mapBreadcrumb), category];
            newState.configurableProduct = product;
            newState.categories = categories;
        } catch (err) {
            console.error('Could not fetch product', err);
        }
    }

    dispatch({ type: 'setState', state: newState });
}
